import { LabelingSnapshot } from 'redux/slices/LabelingSlice'

const STORAGE_PREFIX = 'axon-labeling:'
const STORAGE_VERSION = 1

interface StoredLabeling extends LabelingSnapshot {
  version: number
}

// Labels are keyed by run id, so they are shared across sessions of the same run
// and by the session-less Vet-from-file flow.
const storageKey = (runId: string): string => `${STORAGE_PREFIX}run:${runId}`
// Legacy key: labels used to be keyed by session id (migrated on first load).
const legacySessionKey = (sessionId: number): string => `${STORAGE_PREFIX}${sessionId}`

const readSnapshot = (key: string): LabelingSnapshot | null => {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredLabeling
    // Ignore snapshots from an incompatible schema version
    if (parsed?.version !== STORAGE_VERSION) return null
    return {
      labels: parsed.labels,
      labeledAt: parsed.labeledAt,
      sessionLabels: parsed.sessionLabels,
      activeLabel: parsed.activeLabel,
      dirty: parsed.dirty,
    }
  } catch {
    return null
  }
}

/** Loads a saved labeling snapshot for a run, or null if absent/unreadable. */
export const loadLabeling = (runId: string): LabelingSnapshot | null => readSnapshot(storageKey(runId))

/** Persists a labeling snapshot. No-ops if storage is unavailable or full. */
export const saveLabeling = (runId: string, snapshot: LabelingSnapshot): void => {
  try {
    const stored: StoredLabeling = { version: STORAGE_VERSION, ...snapshot }
    window.localStorage.setItem(storageKey(runId), JSON.stringify(stored))
  } catch {
    // Storage disabled or quota exceeded; export remains the source of truth.
  }
}

/** Removes the saved snapshot for a run. */
export const clearLabeling = (runId: string): void => {
  try {
    window.localStorage.removeItem(storageKey(runId))
  } catch {
    // ignore
  }
}

/**
 * One-time migration from the old session-id-keyed storage. If a run has no
 * run-keyed snapshot yet but a legacy session-keyed one exists, copy it under
 * the run key so existing in-progress labels are not lost. Returns the migrated
 * snapshot when it ran, else null.
 */
export const migrateLegacySession = (
  sessionId: number,
  runId: string
): LabelingSnapshot | null => {
  if (readSnapshot(storageKey(runId))) return null
  const legacy = readSnapshot(legacySessionKey(sessionId))
  if (!legacy) return null
  saveLabeling(runId, legacy)
  return legacy
}

const coverageAckKey = (sessionId: number): string => `${STORAGE_PREFIX}coverage-ack:${sessionId}`

/** Whether the user has dismissed the partial-coverage notice for a session. */
export const hasAcknowledgedCoverage = (sessionId: number): boolean => {
  try {
    return window.localStorage.getItem(coverageAckKey(sessionId)) === '1'
  } catch {
    return false
  }
}

/** Records that the user dismissed the partial-coverage notice for a session. */
export const acknowledgeCoverage = (sessionId: number): void => {
  try {
    window.localStorage.setItem(coverageAckKey(sessionId), '1')
  } catch {
    // ignore
  }
}
